import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  MenuItem,
  Select,
  useTheme,
  useMediaQuery,
  ButtonGroup,
  Tooltip,
} from '@mui/material'
import { useSessionStorageState } from '@toolpad/core'
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SortIcon from '@mui/icons-material/Sort'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { getVehicleSizeOptions } from 'constants/inventory'
import { debounce } from 'lodash'
import { useIsMobile } from '../screen/useIsMobile'
import { ConstructionOutlined } from '@mui/icons-material'
import { Vehicle } from '@otw/models/inventory'
import { GridApi } from 'ag-grid-enterprise'
import { useInventory } from './useInventory'
import { useVirtualizer } from '@tanstack/react-virtual'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import { useSnackbar } from '@/hooks/common/useSnackbar'

interface Filters {
  search: string[]
  vehicleStatus: string[]
  opticTruckWorksOnly: boolean
  type: string[]
  make: string[]
  model: string[]
  size: string[]
  year: string[]
  fuel: string[]
  mileage: [number, number]
  buyPriceRange: [number, number]
  sellPriceRange: [number, number]
  status: string[]
  dealQuality: string[]
}

type FilterKey = keyof Filters
type NumberFilterKey = 'mileage' | 'buyPriceRange' | 'sellPriceRange'
type FilterValue = string[] | [number, number]

const numberFilterLabels: Record<NumberFilterKey, string> = {
  buyPriceRange: 'Buy Price',
  sellPriceRange: 'Sell Price',
  mileage: 'Miles',
}

const getNumberFilterLabel = (key: NumberFilterKey): string => {
  return numberFilterLabels[key]
}

const initialFilters: Filters = {
  search: [],
  vehicleStatus: ['available'], // Default to available vehicles
  opticTruckWorksOnly: false,
  type: [],
  make: [],
  model: [],
  size: [],
  year: [],
  fuel: [],
  mileage: [0, 500000],
  buyPriceRange: [0, 2000000],
  sellPriceRange: [0, 2000000],
  status: [],
  dealQuality: [],
}



// Add type for Vehicle status values
type VehicleStatusValue =
  | 'Pending Available'
  | 'Available'
  | 'Pending Sale'
  | 'Sold'
  | 'Sold elsewhere'
  | 'Active Deal'

// Add sorting types
type SortField =
  | 'date_added'
  | 'year'
  | 'make'
  | 'model'
  | 'size'
  | 'miles'
  | 'optic_list_price'
  | 'seller_asking_price'
  | 'profit'
  | 'stock_number'
  | 'vin'
  | 'status'
  | 'condition'
  | 'type'
  | 'location'

type SortDirection = 'asc' | 'desc'

type SortConfig = {
  field: SortField
  direction: SortDirection
  active: boolean // Add active flag to track if sorting is enabled
}

const SORT_FIELD_LABELS: Record<SortField, string> = {
  date_added: 'Date Added',
  year: 'Year',
  make: 'Make',
  model: 'Model',
  size: 'Size',
  miles: 'Mileage',
  optic_list_price: 'Our Price',
  seller_asking_price: 'Asking Price',
  profit: 'Profit',
  stock_number: 'Stock #',
  vin: 'VIN',
  status: 'Status',
  condition: 'Condition',
  type: 'Type',
  location: 'Location',
}

// Common sort options for mobile - show most useful ones first
const MOBILE_SORT_ORDER: SortField[] = [
  'date_added',
  'year', 
  'make',
  'model',
  'miles',
  'optic_list_price',
  'seller_asking_price',
  'profit',
  'status',
  'size',
  'type',
  'stock_number',
  'vin',
  'condition',
  'location',
]

// Filter Hook
export const useInventoryFilter = (
  inventory_collection: 'master_inventory' | 'shield_inventory' | 'imported_vehicles',
  includeSellPrice: boolean = true,
  isAdmin: boolean = false,
  gridApiRef?: RefObject<GridApi>,
  vehicles?: Vehicle[], // Accept vehicles as parameter instead of fetching
) => {
  const inventoryContainerRef = useRef(null)
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  // Use passed vehicles or fall back to fetching if not provided
  const { vehicles: fetchedVehicles = [], isLoading } = useInventory(inventory_collection)
  const inventory = vehicles || fetchedVehicles

  const [filters, setFilters] = useState<Filters>(initialFilters)
  const [quickFilterText, setQuickFilterText] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'date_added', direction: 'desc', active: true })
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const isMobile = useIsMobile()
  const theme = useTheme()

  const applyFilter = useCallback((key: keyof Filters, value: any) => {
    setFilters(prevFilters => ({ ...prevFilters, [key]: value }))
  }, [])

  const vehicleFilterSet = useMemo(() => {
    return inventory
  }, [inventory])

  // Helper function to filter vehicles with optional exclusions
  const filterVehicles = useCallback((excludeFilters: (keyof Filters)[] = []) => {
    return vehicleFilterSet.filter(vehicle => {
      // Skip poor quality vehicles for non-admins, or if not specifically requested by admins
      // if (vehicle.dealQuality === 'poor' && (!isAdmin || (!filters.dealQuality || !filters.dealQuality.includes('poor')))) {
      //   return false
      // }

      // Vehicle status filtering - skip if excluded
      const matchesVehicleStatus = () => {
        if (excludeFilters.includes('vehicleStatus')) return true
        
        // If no status is selected, show all vehicles
        if (filters.vehicleStatus.length === 0) return true
        
        const vehicleStatusLower = (vehicle.status || '').toLowerCase()
        
        return filters.vehicleStatus.some(status => {
          if (status === 'all') return true
          if (status === 'available') return !vehicleStatusLower.startsWith('sold') && vehicleStatusLower !== 'pending available' && vehicleStatusLower !== 'pending sale' && vehicleStatusLower !== 'active deal'
          if (status === 'sold') return vehicleStatusLower.startsWith('sold')
          if (status === 'pending') return vehicleStatusLower === 'pending available'
          if (status === 'pending_sale') return vehicleStatusLower === 'pending sale' || vehicleStatusLower === 'active deal'
          return false
        })
      }

      const matchesOpticTruckWorks =
        !filters.opticTruckWorksOnly ||
        (vehicle.who != null && vehicle.who.trim().toLowerCase() === 'optic truck works')

      const matchesType =
        filters.type.length === 0 || (vehicle.type && filters.type.includes(vehicle.type.toLowerCase()))

      const matchesMake =
        filters.make.length === 0 || (vehicle.make && filters.make.includes(vehicle.make.toLowerCase()))

      const matchesSize =
        filters.size.length === 0 || (vehicle.size && filters.size.includes(vehicle.size.toLowerCase()))

      const matchesModel =
        filters.model.length === 0 || (vehicle.model && filters.model.includes(vehicle.model.toLowerCase()))

      const matchesYear =
        filters.year.length === 0 || (vehicle.year && filters.year.includes(vehicle.year.toString()))

      const matchesFuel =
        filters.fuel.length === 0 || (vehicle.fuel && filters.fuel.includes(vehicle.fuel.toLowerCase()))

      const matchesMileage =
        vehicle.miles == null || (vehicle.miles >= filters.mileage[0] && vehicle.miles <= filters.mileage[1])

      const matchesBuyPrice =
        vehicle.seller_asking_price == null ||
        (vehicle.seller_asking_price >= filters.buyPriceRange[0] &&
          vehicle.seller_asking_price <= filters.buyPriceRange[1])

      const matchesStatus =
        filters.status.length === 0 || (vehicle.status && filters.status.includes(vehicle.status))

      const matchesSearch =
        !filters.search.length ||
        (() => {
          // Filter out empty strings
          const searchTerms = filters.search.filter(term => term.trim().length > 0)
          
          // If no valid search terms, return true (no search filter)
          if (searchTerms.length === 0) return true
          
          // Check if ANY of the search terms match ANY vehicle field (OR logic)
          return searchTerms.some(searchTerm => {
            const term = searchTerm.toLowerCase().trim()

            // Nested user objects stringified as [object Object] — search name explicitly
            if (vehicle.sold_to_user?.name?.toLowerCase().includes(term)) return true

            return Object.entries(vehicle).some(([key, value]) => {
              if (key === 'link') return false // Skip the link field to not get confusing search results
              if (value != null && typeof value === 'object') return false

              return value?.toString().toLowerCase().includes(term)
            })
          })
        })()

      const matchesSellPrice =
        !includeSellPrice ||
        vehicle.optic_list_price == null ||
        (vehicle.optic_list_price >= filters.sellPriceRange[0] &&
          vehicle.optic_list_price <= filters.sellPriceRange[1])

      const matchesDealQuality =
        filters.dealQuality.length === 0 ||
        (vehicle.dealQuality && filters.dealQuality.includes(vehicle.dealQuality))

      return (
        matchesVehicleStatus() &&
        matchesOpticTruckWorks &&
        matchesType &&
        matchesMake &&
        matchesSize &&
        matchesModel &&
        matchesYear &&
        matchesFuel &&
        matchesMileage &&
        matchesBuyPrice &&
        matchesStatus &&
        matchesSearch &&
        matchesSellPrice &&
        matchesDealQuality
      )
    })
  }, [vehicleFilterSet, filters, includeSellPrice])

  // Helper function to apply sorting
  const applySorting = useCallback((vehicles: Vehicle[]) => {
    // Apply sorting only if active
    if (sortConfig.active) {
      return vehicles.sort((a, b) => {
        const field = sortConfig.field
        const direction = sortConfig.direction === 'asc' ? 1 : -1

        // Handle different data types appropriately
        if (field === 'date_added') {
          const dateA = a.date_added ? new Date(a.date_added).getTime() : 0
          const dateB = b.date_added ? new Date(b.date_added).getTime() : 0
          return (dateB - dateA) * direction
        }

        // Handle numeric fields
        if (['year', 'miles', 'optic_list_price', 'seller_asking_price', 'profit'].includes(field)) {
          const valA = (a[field as keyof Vehicle] as number | null) || 0
          const valB = (b[field as keyof Vehicle] as number | null) || 0
          return (valA - valB) * direction
        }

        // Handle string fields
        const strA = ((a[field as keyof Vehicle] as string | null) || '').toLowerCase()
        const strB = ((b[field as keyof Vehicle] as string | null) || '').toLowerCase()
        return strA.localeCompare(strB) * direction
      })
    }

    // Default sorting by date if sorting is disabled
    return vehicles.sort((a, b) => {
      const dateA = a.date_added ? new Date(a.date_added).getTime() : 0
      const dateB = b.date_added ? new Date(b.date_added).getTime() : 0
      return dateB - dateA
    })
  }, [sortConfig])

  // Filtered inventory for card view (includes vehicleStatus filter)
  const filteredInventory = useMemo(() => {
    const filtered = filterVehicles()
    return applySorting(filtered)
  }, [filterVehicles, applySorting])

  // Filtered inventory for grid view (excludes vehicleStatus filter since grid handles it with tabs)
  const filteredInventoryForGrid = useMemo(() => {
    const filtered = filterVehicles(['vehicleStatus'])
    return applySorting(filtered)
  }, [filterVehicles, applySorting])

  // Filtered inventory for mobile grid view (includes vehicleStatus filter)
  const filteredInventoryForMobileGrid = useMemo(() => {
    const filtered = filterVehicles()
    return applySorting(filtered)
  }, [filterVehicles, applySorting])

  const SearchBarUI = () => {
    const [searchValue, setSearchValue] = useState('')

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value)
    }

    const handleSearch = () => {
      const terms = searchValue.split('\n').filter(term => term.trim().length > 0)
      setFilters(prevFilters => ({ ...prevFilters, search: terms }))
    }

    const handleClear = () => {
      setSearchValue('')
      setFilters(prevFilters => ({ ...prevFilters, search: [] }))
    }

    const handleAddTerm = () => {
      const trimmedValue = searchValue.trim()
      if (trimmedValue.length > 0) {
        setFilters(prevFilters => ({ 
          ...prevFilters, 
          search: [...prevFilters.search, trimmedValue]
        }))
        setSearchValue('')
      }
    }

    const handleRemoveTerm = (termToRemove: string) => {
      setFilters(prevFilters => ({
        ...prevFilters,
        search: prevFilters.search.filter(term => term !== termToRemove)
      }))
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAddTerm()
      }
    }

    return (
      <Box
        mt={isMobile ? 0 : 2}
        mb={isMobile ? 0 : 2}
        width='100%'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'stretch',
          px: isMobile ? 1 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <TextField
            fullWidth
            placeholder='Search inventory'
            value={searchValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            size={isMobile ? 'small' : 'medium'}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position='end'>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      {searchValue && (
                        <IconButton
                          onClick={() => setSearchValue('')}
                          size='small'
                          sx={{ p: isMobile ? 0.5 : 1 }}
                        >
                          <CloseIcon sx={{ fontSize: isMobile ? '16px' : '20px' }} />
                        </IconButton>
                      )}
                      <Tooltip title="Add search term">
                        <span>
                          <IconButton
                            onClick={handleAddTerm}
                            size='small'
                            color='primary'
                            disabled={!searchValue.trim()}
                            sx={{ p: isMobile ? 0.5 : 1 }}
                          >
                            <SearchIcon sx={{ fontSize: isMobile ? '16px' : '20px' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: '1 1 auto',
              minWidth: isMobile ? 0 : 200,
              height: isMobile ? '40px' : '56px',
              '& .MuiInputBase-root': {
                height: isMobile ? '40px' : '56px',
              },
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '14px' : '16px',
                padding: isMobile ? '8px 12px' : '16.5px 14px',
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.opticTruckWorksOnly}
                onChange={e =>
                  setFilters(prev => ({ ...prev, opticTruckWorksOnly: e.target.checked }))
                }
                size={isMobile ? 'small' : 'medium'}
              />
            }
            label="Optic"
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          />
        </Box>

        {/* Search Term Chips */}
        {filters.search.length > 0 && (
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mt: isMobile ? 1 : 2,
              mb: isMobile ? 0 : 2, 
              width: '100%',
              maxWidth: isMobile ? '100%' : 'none',
              minWidth: isMobile ? 'auto' : '400px',
            }}
          >
            {filters.search.map((term, index) => (
              <Chip
                key={`${term}-${index}`}
                label={term}
                onDelete={() => handleRemoveTerm(term)}
                color='primary'
                variant='outlined'
                size={isMobile ? 'small' : 'medium'}
              />
            ))}
            {filters.search.length > 1 && (
              <Chip
                label='Clear All'
                onClick={handleClear}
                color='secondary'
                variant='outlined'
                size={isMobile ? 'small' : 'medium'}
              />
            )}
          </Box>
        )}
      </Box>
    )
  }

  const FilterUI = () => {
    const [expandedAccordions, setExpandedAccordions] = useSessionStorageState<string[]>(
      'filter_opened-accordions',
      [],
      {
        codec: {
          stringify: JSON.stringify,
          parse: storedValue => {
            try {
              return JSON.parse(storedValue)
            } catch (error) {
              return []
            }
          },
        },
      },
    )
    const [mobileFilters, setMobileFilters] = useState<Filters>(filters)
    const availableMakes = Array.from(
      new Set(vehicleFilterSet.map(vehicle => safeToLowerCase(vehicle.make)).filter(Boolean)),
    )
    const availableTypes = Array.from(
      new Set(vehicleFilterSet.map(vehicle => safeToLowerCase(vehicle.type)).filter(Boolean)),
    )
    const availableSizes = Array.from(
      new Set(vehicleFilterSet.map(vehicle => safeToLowerCase(vehicle.size)).filter(Boolean)),
    )

    const sizeOptions = useMemo(() => {
      if (isMobile && mobileFilters.type.length > 0) {
        const typeSpecificSizes = new Set(mobileFilters.type.flatMap(type => getVehicleSizeOptions(type)))
        return Array.from(new Set([...availableSizes].filter(size => typeSpecificSizes.has(size))))
      } else if (!isMobile && filters.type.length > 0) {
        const typeSpecificSizes = new Set(filters.type.flatMap(type => getVehicleSizeOptions(type)))
        return Array.from(new Set([...availableSizes].filter(size => typeSpecificSizes.has(size))))
      }

      return availableSizes
    }, [availableSizes, filters.type, mobileFilters.type])

    const availableYears = Array.from(
      new Set(
        vehicleFilterSet
          .map(vehicle => vehicle.year?.toString())
          .filter((year): year is string => year !== undefined)
          .sort((num1, num2) => Number(num2) - Number(num1)),
      ),
    )
    const availableModels = Array.from(
      new Set(vehicleFilterSet.map(vehicle => safeToLowerCase(vehicle.model)).filter(Boolean)),
    )
    const availableFuels = Array.from(
      new Set(vehicleFilterSet.map(vehicle => safeToLowerCase(vehicle.fuel)).filter(Boolean)),
    )

    const availableStatuses = Array.from(
      new Set(
        vehicleFilterSet
          .map(vehicle => vehicle.status)
          .filter((status): status is VehicleStatusValue => status !== null && status !== undefined),
      ),
    )

    const availableDealQualities = Array.from(
      new Set(vehicleFilterSet.map(vehicle => safeToLowerCase(vehicle.dealQuality)).filter(Boolean)),
    )

    // const isFilterActive = (filterType: FilterKey): boolean => {
    //   return !_.isEqual(filters[filterType], initialFilters[filterType])
    // }

    const getActiveOptionCount = (filters: Filters, filterType: FilterKey): number => {
      const filter = filters[filterType]
      if (Array.isArray(filter)) {
        return filter.length
      }
      return filter !== initialFilters[filterType] ? 1 : 0
    }

    const isAccordionExpanded = useCallback(
      (accordionKey: string) => {
        if (expandedAccordions === null) return false

        return expandedAccordions.includes(accordionKey)
      },
      [expandedAccordions],
    )

    const handleAccordionToggle = useCallback(
      (accordionKey: string) => {
        setExpandedAccordions(prev => {
          if (prev === null) return [accordionKey]
          return prev.includes(accordionKey) ? prev.filter(key => key !== accordionKey) : [...prev, accordionKey]
        })
      },
      [setExpandedAccordions],
    )

    const handleFilterClick = (filterType: FilterKey, value: string) => {
      if (isMobile) {
        setMobileFilters(prevFilters => {
          // Special handling for vehicleStatus - only allow single selection
          if (filterType === 'vehicleStatus') {
            const currentFilter = prevFilters[filterType] as string[]
            const updatedFilter = currentFilter.includes(value) ? [] : [value]
            return {
              ...prevFilters,
              [filterType]: updatedFilter,
            }
          }
          
          // Multi-select for other filters
          const currentFilter = prevFilters[filterType] as string[]
          const updatedFilter = currentFilter.includes(value)
            ? currentFilter.filter(item => item !== value)
            : [...currentFilter, value]

          return {
            ...prevFilters,
            [filterType]: updatedFilter,
          }
        })
      } else {
        setFilters(prevFilters => {
          // Special handling for vehicleStatus - only allow single selection
          if (filterType === 'vehicleStatus') {
            const currentFilter = prevFilters[filterType] as string[]
            const updatedFilter = currentFilter.includes(value) ? [] : [value]
            return {
              ...prevFilters,
              [filterType]: updatedFilter,
            }
          }
          
          // Multi-select for other filters
          const currentFilter = prevFilters[filterType] as string[]
          const updatedFilter = currentFilter.includes(value)
            ? currentFilter.filter(item => item !== value)
            : [...currentFilter, value]

          return {
            ...prevFilters,
            [filterType]: updatedFilter,
          }
        })
      }
    }

    const hasValidOptions = (options: string[]): boolean => {
      return options.some(option => option.trim() !== '')
    }

    const FilterButtonGrid = ({ options, filterType }: { options: string[]; filterType: FilterKey }) => (
      <Grid container spacing={1}>
        {options.map(option => (
          <Grid item key={option}>
            <Button
              variant={
                ((isMobile ? mobileFilters[filterType] : filters[filterType]) as string[]).includes(option)
                  ? 'contained'
                  : 'outlined'
              }
              size='small'
              onClick={() => handleFilterClick(filterType, option)}
            >
              {option}
            </Button>
          </Grid>
        ))}
      </Grid>
    )

    const renderNumberRangeFilter = (label: string, filterKey: 'mileage' | 'buyPriceRange' | 'sellPriceRange') => {
      const [localMin, setLocalMin] = useState(String((filters[filterKey] as [number, number])[0]))
      const [localMax, setLocalMax] = useState(String((filters[filterKey] as [number, number])[1]))

      const handleBlur = () => {
        const min = Number(localMin)
        const max = Number(localMax)
        if (isMobile) {
          setMobileFilters(prevFilters => {
            return {
              ...prevFilters,
              [filterKey]: [min, max],
            }
          })
        } else {
          applyFilter(filterKey, [min, max])
        }
      }

      return (
        <Box display='flex' flexDirection='row' gap={2}>
          <TextField
            label={`Min ${label}`}
            variant='outlined'
            type='number'
            value={localMin}
            onChange={e => setLocalMin(e.target.value)}
            onBlur={handleBlur}
          />
          <TextField
            label={`Max ${label}`}
            variant='outlined'
            type='number'
            value={localMax}
            onChange={e => setLocalMax(e.target.value)}
            onBlur={handleBlur}
          />
        </Box>
      )
    }

    const ActiveFilterBadge = ({ filterType, children }: { filterType: FilterKey; children: React.ReactNode }) => {
      const count = getActiveOptionCount(isMobile ? mobileFilters : filters, filterType)

      return (
        <Box display='flex' alignItems='center' width='100%' justifyContent='space-between'>
          <Typography>{children}</Typography>
          {count > 0 && <Badge badgeContent={count} color='primary' showZero={false} />}
        </Box>
      )
    }

    return (
      <Box>
        {/* Vehicle Status Filter - Only show on desktop */}
        {!isMobile && (
          <Accordion expanded={isAccordionExpanded('vehicleStatus')} onChange={() => handleAccordionToggle('vehicleStatus')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('vehicleStatus') ? 4 : 2,
                  width: '100%',
                },
              }}
            >
              <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip
                    title="All filters you apply here will filter grid view as well, except for vehicle status, which is handled by tabs in grid view"
                    placement="top"
                    arrow
                  >
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      <InfoIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                  <Typography>Vehicle Status</Typography>
                </Box>
                {(() => {
                  const count = getActiveOptionCount(isMobile ? mobileFilters : filters, 'vehicleStatus')
                  return count > 0 ? (
                    <Badge badgeContent={count} color='primary' showZero={false} />
                  ) : null
                })()}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={['all', 'available', 'sold']} filterType='vehicleStatus' />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Quick filter: Who = Optic Truck Works */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isMobile ? mobileFilters.opticTruckWorksOnly : filters.opticTruckWorksOnly}
                onChange={(e) => {
                  const checked = e.target.checked
                  if (isMobile) {
                    setMobileFilters((prev) => ({ ...prev, opticTruckWorksOnly: checked }))
                  } else {
                    applyFilter('opticTruckWorksOnly', checked)
                  }
                }}
                color="primary"
                size="small"
              />
            }
            label="Optic Truck Works only"
          />
        </Box>

        {hasValidOptions(availableTypes) && (
          <Accordion expanded={isAccordionExpanded('type')} onChange={() => handleAccordionToggle('type')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('type') ? 4 : 2,
                  width: '100%',
                },
              }}
            >
              <ActiveFilterBadge filterType='type'>Type</ActiveFilterBadge>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={availableTypes} filterType='type' />
            </AccordionDetails>
          </Accordion>
        )}
        {hasValidOptions(sizeOptions) && (
          <Accordion expanded={isAccordionExpanded('size')} onChange={() => handleAccordionToggle('size')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('size') ? 3 : 2,
                  width: '100%',
                },
              }}
            >
              <ActiveFilterBadge filterType='size'>Size</ActiveFilterBadge>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={sizeOptions} filterType='size' />
            </AccordionDetails>
          </Accordion>
        )}
        {hasValidOptions(availableMakes) && (
          <Accordion expanded={isAccordionExpanded('make')} onChange={() => handleAccordionToggle('make')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('make') ? 3 : 2,
                  width: '100%',
                },
              }}
            >
              <ActiveFilterBadge filterType='make'>Make</ActiveFilterBadge>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={availableMakes} filterType='make' />
            </AccordionDetails>
          </Accordion>
        )}

        {hasValidOptions(availableModels) && (
          <Accordion expanded={isAccordionExpanded('model')} onChange={() => handleAccordionToggle('model')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('model') ? 3 : 2,
                  width: '100%',
                },
              }}
            >
              <ActiveFilterBadge filterType='model'>Model</ActiveFilterBadge>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={availableModels} filterType='model' />
            </AccordionDetails>
          </Accordion>
        )}
        {hasValidOptions(availableYears) && (
          <Accordion expanded={isAccordionExpanded('year')} onChange={() => handleAccordionToggle('year')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('year') ? 3 : 2,
                  width: '100%',
                },
              }}
            >
              <ActiveFilterBadge filterType='year'>Year</ActiveFilterBadge>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={availableYears} filterType='year' />
            </AccordionDetails>
          </Accordion>
        )}
        {hasValidOptions(availableFuels) && (
          <Accordion expanded={isAccordionExpanded('fuel')} onChange={() => handleAccordionToggle('fuel')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('fuel') ? 3 : 2,
                  width: '100%',
                },
              }}
            >
              <ActiveFilterBadge filterType='fuel'>Fuel</ActiveFilterBadge>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={availableFuels} filterType='fuel' />
            </AccordionDetails>
          </Accordion>
        )}

        {hasValidOptions(availableStatuses) && (
          <Accordion expanded={isAccordionExpanded('status')} onChange={() => handleAccordionToggle('status')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('status') ? 3 : 2,
                  width: '100%',
                },
              }}
            >
              <ActiveFilterBadge filterType='status'>Status</ActiveFilterBadge>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={availableStatuses as string[]} filterType='status' />
            </AccordionDetails>
          </Accordion>
        )}

        <Accordion expanded={isAccordionExpanded('mileage')} onChange={() => handleAccordionToggle('mileage')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Mileage</AccordionSummary>
          <AccordionDetails>{renderNumberRangeFilter('Mileage', 'mileage')}</AccordionDetails>
        </Accordion>

        <Accordion
          expanded={isAccordionExpanded('buyPriceRange')}
          onChange={() => handleAccordionToggle('buyPriceRange')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Buy Price</AccordionSummary>
          <AccordionDetails>{renderNumberRangeFilter('Buy Price', 'buyPriceRange')}</AccordionDetails>
        </Accordion>
        {includeSellPrice && (
          <Accordion
            expanded={isAccordionExpanded('sellPriceRange')}
            onChange={() => handleAccordionToggle('sellPriceRange')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Sell Price</AccordionSummary>
            <AccordionDetails>{renderNumberRangeFilter('Sell Price', 'sellPriceRange')}</AccordionDetails>
          </Accordion>
        )}
        {isAdmin && hasValidOptions(availableDealQualities) && (
          <Accordion
            expanded={isAccordionExpanded('dealQuality')}
            onChange={() => handleAccordionToggle('dealQuality')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  marginRight: isAccordionExpanded('dealQuality') ? 3 : 2,
                  width: '100%',
                },
              }}
            >
              <ActiveFilterBadge filterType='dealQuality'>Deal Quality</ActiveFilterBadge>
            </AccordionSummary>
            <AccordionDetails>
              <FilterButtonGrid options={availableDealQualities} filterType='dealQuality' />
            </AccordionDetails>
          </Accordion>
        )}
        {isMobile && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Button
              variant='outlined'
              color='primary'
              onClick={() => {
                // Clear local changes without applying
                setMobileFilters(filters)
                // Close drawer if needed
                if (typeof window !== 'undefined') {
                  // Dispatch an event that can be caught by the parent component
                  window.dispatchEvent(new CustomEvent('closeFilterDrawer'))
                }
              }}
            >
              Close
            </Button>
            <Button
              fullWidth
              variant='contained'
              color='primary'
              onClick={() => {
                setFilters(mobileFilters)
                // Close drawer if needed
                if (typeof window !== 'undefined') {
                  // Dispatch an event that can be caught by the parent component
                  window.dispatchEvent(new CustomEvent('closeFilterDrawer'))
                }
              }}
            >
              Apply Filters
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  const getActiveFilters = (filters: Filters): [FilterKey, string[] | [number, number] | boolean][] => {
    return (Object.entries(filters) as [FilterKey, string[] | [number, number] | boolean][]).filter(([key, value]) => {
      if (key === 'search' || key === 'vehicleStatus') return false

      const initialValue = initialFilters[key as keyof typeof initialFilters]

      if (key === 'opticTruckWorksOnly') {
        return value === true
      }
      if (Array.isArray(value) && Array.isArray(initialValue)) {
        return value[0] !== initialValue[0] || value[1] !== initialValue[1]
      }

      return value !== initialValue
    })
  }

  const ResetChip = () => {
    return <Chip key='reset-chip' label='Reset' onClick={() => setFilters(initialFilters)} variant='outlined' />
  }

  const FilterChip = ({ filterKey, value }: { filterKey: FilterKey; value: FilterValue | boolean }) => {
    if (value === undefined || value === null) return null

    if (filterKey === 'opticTruckWorksOnly' && value === true) {
      return (
        <Chip
          key="opticTruckWorksOnly"
          label="Optic Truck Works only"
          onDelete={() => removeFilter('opticTruckWorksOnly')}
          color="primary"
          variant="outlined"
        />
      )
    }

    if (isNumberPair(value as FilterValue)) {
      const numVal = value as [number, number]
      if (numVal[0] === undefined || numVal[1] === undefined) return null

      return (
        <Chip
          key={`${filterKey}-${numVal[0]}-${numVal[1]}`}
          label={`${getNumberFilterLabel(filterKey as NumberFilterKey)}: ${new Intl.NumberFormat('en-US').format(numVal[0])}-${new Intl.NumberFormat('en-US').format(numVal[1])}`}
          onDelete={() => removeFilter(filterKey)}
          color='primary'
          variant='outlined'
        />
      )
    } else {
      const arrVal = value as string[]
      if (!Array.isArray(arrVal) || arrVal.length === 0) return null

      return (
        <>
          {arrVal
            .filter(item => item !== undefined && item !== '')
            .map((item, index) => (
              <Chip
                key={`${filterKey}-${item}-${index}`}
                label={item}
                onDelete={() => removeFilter(filterKey, item)}
                color='primary'
                variant='outlined'
              />
            ))}
        </>
      )
    }
  }



  const removeFilter = (filterType: FilterKey, value?: string) => {
    setFilters(prevFilters => {
      if (filterType === 'opticTruckWorksOnly') {
        return { ...prevFilters, opticTruckWorksOnly: false }
      }
      if (value === undefined) {
        // Remove all filters of this type
        return {
          ...prevFilters,
          [filterType]: initialFilters[filterType],
        }
      } else {
        // Remove individual filter
        const currentFilter = prevFilters[filterType] as string[]
        return {
          ...prevFilters,
          [filterType]: currentFilter.filter(item => item !== value),
        }
      }
    })
  }

  // Mobile Vehicle Status Toggle
  const MobileVehicleStatusToggle = () => {
    const currentStatus = filters.vehicleStatus.length > 0 ? filters.vehicleStatus[0] : 'available'
    
    const handleCycleToggle = () => {
      const nextStatus = currentStatus === 'all' ? 'available' :
                        currentStatus === 'available' ? 'pending' :
                        currentStatus === 'pending' ? 'pending_sale' :
                        currentStatus === 'pending_sale' ? 'sold' : 'all'
      setFilters(prevFilters => ({
        ...prevFilters,
        vehicleStatus: [nextStatus],
      }))
    }

    const getButtonConfig = () => {
      switch (currentStatus) {
        case 'all':
          return {
            text: 'All',
            color: theme.palette.primary.main,
            bgColor: `${theme.palette.primary.main}1A`, // 10% opacity
          }
        case 'available':
          return {
            text: 'Available',
            color: theme.palette.success.main,
            bgColor: `${theme.palette.success.main}1A`,
          }
        case 'sold':
          return {
            text: 'Sold',
            color: theme.palette.error.main,
            bgColor: `${theme.palette.error.main}1A`,
          }
        case 'pending':
          return {
            text: 'Pending Avail.',
            color: theme.palette.warning.main,
            bgColor: `${theme.palette.warning.main}1A`,
          }
        case 'pending_sale':
          return {
            text: 'Pending Sale',
            color: theme.palette.info.main,
            bgColor: `${theme.palette.info.main}1A`,
          }
        default:
          return {
            text: 'Available',
            color: theme.palette.success.main,
            bgColor: `${theme.palette.success.main}1A`,
          }
      }
    }

    if (!isMobile) return null

    const config = getButtonConfig()

    return (
      <Button
        variant='outlined'
        size='small'
        onClick={handleCycleToggle}
        sx={{
          textTransform: 'none',
          borderColor: config.color,
          color: config.color,
          backgroundColor: config.bgColor,
          '&:hover': {
            backgroundColor: `${config.color}30`, // 20% opacity on hover
            borderColor: config.color,
          },
          minWidth: '80px',
          fontSize: '0.75rem',
        }}
      >
        {config.text}
      </Button>
    )
  }

  // Create a new SortUI component with toggle functionality
  const SortUI = ({ compact = false }: { compact?: boolean } = {}) => {
    const [isExpanded, setIsExpanded] = useState(!isMobile && !compact)

    // Compact horizontal layout for desktop pagination area
    if (compact && !isMobile) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: '1px solid',
            borderColor: sortConfig.active ? theme.palette.primary.main : theme.palette.divider,
            borderRadius: 1,
            px: 1,
            py: 0.5,
            minHeight: '40px',
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              setSortConfig({
                ...sortConfig,
                active: !sortConfig.active,
              })
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 1,
                p: 0.25,
                mr: 0.5,
                bgcolor: sortConfig.active ? 'primary.main' : 'transparent',
                color: sortConfig.active ? 'white' : 'text.secondary',
                transition: 'all 0.2s ease',
              }}
            >
              <SortIcon fontSize='small' />
            </Box>
            <Typography
              variant='body2'
              color={sortConfig.active ? 'primary' : 'text.secondary'}
              fontWeight={sortConfig.active ? 500 : 400}
              sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            >
              {isMobile && sortConfig.active ? 'Sort by' : 'Sort'}
            </Typography>
          </Box>

          {sortConfig.active && (
            <>
              <Select
                value={sortConfig.field}
                onChange={e =>
                  setSortConfig({
                    ...sortConfig,
                    field: e.target.value as SortField,
                  })
                }
                size='small'
                sx={{
                  minWidth: isMobile ? 130 : 120,
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                  },
                }}
                MenuProps={{
                  sx: {
                    maxHeight: 400,
                  },
                }}
              >
                {(isMobile ? MOBILE_SORT_ORDER : Object.keys(SORT_FIELD_LABELS).sort((a, b) => 
                  SORT_FIELD_LABELS[a as SortField].localeCompare(SORT_FIELD_LABELS[b as SortField])
                )).map((field) => (
                  <MenuItem key={field} value={field}>
                    {SORT_FIELD_LABELS[field as SortField]}
                  </MenuItem>
                ))}
              </Select>

              <IconButton
                size='small'
                onClick={e => {
                  e.stopPropagation()
                  setSortConfig({
                    ...sortConfig,
                    direction: sortConfig.direction === 'asc' ? 'desc' : 'asc',
                  })
                }}
                sx={{ p: 0.5 }}
              >
                {sortConfig.direction === 'asc' ? (
                  <ArrowUpwardIcon fontSize='small' color='primary' />
                ) : (
                  <ArrowDownwardIcon fontSize='small' color='primary' />
                )}
              </IconButton>
            </>
          )}
        </Box>
      )
    }

    // Original vertical layout for mobile and sidebar
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: isMobile ? 0 : 1,
          border: '1px solid',
          borderColor: sortConfig.active ? theme.palette.primary.main : theme.palette.divider,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Sort Toggle Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            py: 0.5,
            px: 1,
            bgcolor: 'transparent',
            transition: 'background-color 0.2s ease',
          }}
          onClick={() => {
            if (sortConfig.active) {
              setIsExpanded(!isExpanded)
            } else {
              // If sort is inactive and user clicks, activate it and expand
              setSortConfig({
                ...sortConfig,
                active: true,
              })
              setIsExpanded(true)
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 1,
                p: 0.5,
                mr: 1,
                bgcolor: sortConfig.active ? 'primary.main' : 'transparent',
                color: sortConfig.active ? 'white' : 'text.secondary',
                transition: 'all 0.2s ease',
              }}
              onClick={e => {
                e.stopPropagation()
                setSortConfig({
                  ...sortConfig,
                  active: !sortConfig.active,
                })
                // If activating sort, also expand
                if (!sortConfig.active) {
                  setIsExpanded(true)
                }
              }}
            >
              <SortIcon fontSize='small' />
            </Box>

            <Typography
              variant='body2'
              color={sortConfig.active ? 'primary' : 'text.secondary'}
              fontWeight={sortConfig.active ? 500 : 400}
            >
              {sortConfig.active ? SORT_FIELD_LABELS[sortConfig.field] : 'Sort'}
            </Typography>
          </Box>

          {sortConfig.active && (
            <IconButton
              size='small'
              onClick={e => {
                e.stopPropagation()
                setSortConfig({
                  ...sortConfig,
                  direction: sortConfig.direction === 'asc' ? 'desc' : 'asc',
                })
              }}
            >
              {sortConfig.direction === 'asc' ? (
                <ArrowUpwardIcon fontSize='small' color='primary' />
              ) : (
                <ArrowDownwardIcon fontSize='small' color='primary' />
              )}
            </IconButton>
          )}
        </Box>

        {/* Expanded Sort Controls */}
        {isExpanded && sortConfig.active && (
          <Box
            sx={{
              p: 1,
              borderTop: '1px solid',
              borderColor: theme.palette.divider,
              bgcolor: 'background.paper',
            }}
          >
            <Select
              value={sortConfig.field}
              onChange={e =>
                setSortConfig({
                  ...sortConfig,
                  field: e.target.value as SortField,
                })
              }
              size='small'
              fullWidth
              sx={{
                minWidth: isMobile ? '100%' : 150,
              }}
              MenuProps={{
                sx: {
                  maxHeight: 400,
                },
              }}
            >
              {(isMobile ? MOBILE_SORT_ORDER : Object.keys(SORT_FIELD_LABELS).sort((a, b) => 
                SORT_FIELD_LABELS[a as SortField].localeCompare(SORT_FIELD_LABELS[b as SortField])
              )).map((field) => (
                <MenuItem key={field} value={field}>
                  {SORT_FIELD_LABELS[field as SortField]}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>
    )
  }


  const clearAllFilters = useCallback(() => {
    if (gridApiRef?.current) {
      setQuickFilterText('')
      gridApiRef.current.setFilterModel(null)
      
      // Only clear sorting, preserve column visibility from template
      const currentColumnState = gridApiRef.current.getColumnState()
      const clearedSortState = currentColumnState.map(col => ({
        ...col,
        sort: null
      }))
      
      // Add a small delay to ensure template application is complete
      setTimeout(() => {
        if (gridApiRef?.current && !gridApiRef.current.isDestroyed()) {
          gridApiRef.current.applyColumnState({
            state: clearedSortState,
            applyOrder: false, // Don't apply order to preserve template column order
          })
        }
      }, 100)
      
      gridApiRef.current.setRowGroupColumns([])
      gridApiRef.current.setPivotColumns([])
      gridApiRef.current.setValueColumns([])

      if (gridApiRef.current.setAdvancedFilterModel) {
        gridApiRef.current.setAdvancedFilterModel(null)
      }

      requestAnimationFrame(() => {
        if (gridApiRef.current) {
          gridApiRef.current.refreshCells({ force: true })
          gridApiRef.current.onFilterChanged()

          const hasAnyFilters =
            gridApiRef.current.isAnyFilterPresent() ||
            gridApiRef.current.getRowGroupColumns()?.length > 0 ||
            gridApiRef.current.getPivotColumns()?.length > 0

          setHasActiveFilters(hasAnyFilters)
        }
      })

      showSuccess('All filters cleared')
    }
  }, [showSnackbar, setQuickFilterText, setHasActiveFilters])

  useEffect(() => {
    if (!gridApiRef?.current) return

    const api = gridApiRef.current
    if (api.isDestroyed()) return

    const checkFilters = () => {
      if (!api || api.isDestroyed()) return

      try {
        const hasColumnFilters = api.isAnyFilterPresent()
        const hasQuickFilter = !!quickFilterText
        const hasRowGrouping = (api.getRowGroupColumns()?.length || 0) > 0
        const hasPivotColumns = (api.getPivotColumns()?.length || 0) > 0
        const hasColumnSorting = api.getColumnState().some(col => col.sort)
        const hasValueColumns = (api.getValueColumns()?.length || 0) > 0

        const hasAnyFilters =
          hasColumnFilters ||
          hasQuickFilter ||
          hasRowGrouping ||
          hasPivotColumns ||
          hasColumnSorting ||
          hasValueColumns

        setHasActiveFilters(hasAnyFilters)
      } catch (error) {
        console.error('Error checking filters:', error)
      }
    }

    // Wait a bit for grid to be fully ready
    const timeoutId = setTimeout(() => {
      checkFilters()

      // Add listeners for all relevant grid events
      const events = [
        'filterChanged',
        'columnRowGroupChanged',
        'columnPivotChanged',
        'sortChanged',
        'columnValueChanged',
        'gridColumnsChanged',
        'modelUpdated',
      ]

      events.forEach(event => api.addEventListener(event as any, checkFilters))
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      if (!api.isDestroyed()) {
        const events = [
          'filterChanged',
          'columnRowGroupChanged',
          'columnPivotChanged',
          'sortChanged',
          'columnValueChanged',
          'gridColumnsChanged',
          'modelUpdated',
        ]
        events.forEach(event => api.removeEventListener(event as any, checkFilters))
      }
    }
  }, [gridApiRef?.current, quickFilterText, setHasActiveFilters])

  // Responsive breakpoints
  const isXs = useMediaQuery(theme.breakpoints.only('xs'))
  const isSm = useMediaQuery(theme.breakpoints.only('sm'))
  const isMd = useMediaQuery(theme.breakpoints.only('md'))
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'))

  const columnCount = useMemo(() => {
    if (isXs) return 1
    if (isSm) return 2
    if (isMd) return 3
    if (isLgUp) return 4
    return 4
  }, [isXs, isSm, isMd, isLgUp])

  const renderRows = useMemo(() => {
    return Array.from({ length: Math.ceil(filteredInventory.length / columnCount) }, (_, rowIndex) =>
      filteredInventory.slice(rowIndex * columnCount, rowIndex * columnCount + columnCount),
    )
  }, [filteredInventory, columnCount])

  const rowVirtualizer = useVirtualizer({
    count: renderRows.length,
    getScrollElement: () => inventoryContainerRef.current,
    estimateSize: () => (columnCount === 1 ? 145 : 700), // Updated from 170 to 145 for new card height
    overscan: columnCount === 1 ? 3 : 1, // More overscan on mobile for better skeleton experience
  })

  // Create compact sort UI for desktop pagination area
  const CompactSortUI = () => SortUI({ compact: true })

  return {
    filteredInventory,
    filteredInventoryForGrid,
    filteredInventoryForMobileGrid,
    initialFilters,
    filters,
    activeFilters: getActiveFilters(filters),
    FilterChip,
    ResetChip,
    setFilters,
    FilterUI,
    SearchBarUI,
    MobileVehicleStatusToggle,
    removeFilter,
    SortUI,
    CompactSortUI,
    clearAllFilters,
    sortConfig,
    setSortConfig,
    quickFilterText,
    setQuickFilterText,
    hasActiveFilters,
    setHasActiveFilters,
    columnCount,
    renderRows,
    rowVirtualizer,
    inventoryContainerRef,
  }
}

const isNumberPair = (value: FilterValue): value is [number, number] =>
  Array.isArray(value) && value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number'

const safeToLowerCase = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null) return ''

  return value.toString().toLowerCase()
}
